import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { role } from "../../Constants/role";
import Select from "react-tailwindcss-select";
import { getCities } from "../../Services/citiesApi";
import { set } from "lodash-es";

const AdminRoleModal = ({ admin, onClose, onSave, isSuperAdmin }) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null); // For Super City Admin
  const [citiesList, setCitiesList] = useState([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setSelectedRole(role.contentCreator);
    }
  }, [admin]);

  const handleRoleChange = (option) => {
    setSelectedRole(Number(option.value));
    // setSelectedCity(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let roleData = {
        roleId: selectedRole,
      };

      // If Super City Admin, send single cityId
      if (selectedRole === role.superCityAdmin && isSuperAdmin) {
        roleData.cityId = selectedCity?.value;
      }
      // If City Admin, send multiple citiesIds
      else if ((selectedRole === role.contentCreator || selectedRole === role.TerminalAdmin) && isSuperAdmin) {
        roleData.cityId = selectedCity?.map((c) => c.value) || "";
      }

      await onSave(roleData);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };
  const handleCityChange = (value) => {
    setSelectedCity(value);
  };

  // Filter out already selected cities from the options (for multi-select)
  const availableCitiesOptions = useMemo(() => {
    if (!citiesList || citiesList.length === 0) return [];
    
    // For multi-select (contentCreator or TerminalAdmin), filter out selected cities
    if (selectedRole === role.contentCreator || selectedRole === role.TerminalAdmin) {
      if (!selectedCity || !Array.isArray(selectedCity) || selectedCity.length === 0) {
        return citiesList;
      }
      
      // Get IDs of already selected cities
      const selectedCityIds = selectedCity.map((city) => city.value);
      
      // Filter out already selected cities
      return citiesList.filter(
        (city) => !selectedCityIds.includes(city.value)
      );
    }
    
    // For single select (superCityAdmin), return all cities
    return citiesList;
  }, [citiesList, selectedCity, selectedRole]);
  
  useEffect(() => {
    if (selectedRole === role.contentCreator) {
      setSelectedCity([]);
    }
    //for already assign city for city  admin
    else if( selectedRole === role.TerminalAdmin){
    let data = admin?.cities?.map((c) => {
      return {
        value: c.cityId,
        label: c.cityName,
      };
    });
    setSelectedCity(data);

    }
     else {
      setSelectedCity(null);
    }
  }, [selectedRole]);

const fetchCities = useCallback(async () => {
  try {
    const citiesResponse = await getCities();
    const cities = citiesResponse?.data?.data || [];

    let result = [];

    if (selectedRole === role.contentCreator) {
      result = cities
        ?.filter((city) =>
          admin?.cities?.some(
            (adminCity) => adminCity.cityId === city.id
          )
        )
        .map((city) => ({
          value: city.id,
          label: city.name,
        }));
    } 
    else {
      result = cities?.map((city) => ({
        value: city.id,
        label: city.name,
      }));
    }
    setCitiesList(result);
  } catch (e) {
    console.error("Error fetching cities:", e);
    setError("Failed to load cities");
  }
}, [admin?.cities, selectedRole]);

  
useEffect(() => {
  fetchCities();
}, [fetchCities]);

 
  const roleOptions = isSuperAdmin
    ? [
        { value: role.contentCreator, label: t("citizen") },
        { value: role.superCityAdmin, label: t("superCityAdmins") },
        { value: role.TerminalAdmin, label: t("city_admin") },
      ]
    : [
        // { value: role.TerminalAdmin, label: t("city_admin") || "City Admin" },
        { value: role.contentCreator, label: t("citizen") },
      ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full relative"  
      onMouseDown={(e) => {
        e.stopPropagation(); // ✅ THIS IS THE KEY
        }}>
        <button
          className="absolute top-2 right-3 text-gray-500 hover:text-black text-2xl"
          onMouseDown={(e) => {
            e.preventDefault(); // ✅ KEY
            e.stopPropagation(); // ✅ KEY
            onClose(); // close modal immediately
          }}
        >
          &times;
        </button>

        <h2 className="text-2xl font-bold mb-4 text-center">
          {t("change_city_admin_role") || "Change City Admin Role"}
        </h2>

        <div className="mb-4 p-3 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>{t("city_admin") || "City Admin"}:</strong> {admin?.name}
          </p>
          <p className="text-sm text-gray-600">
            <strong>{t("email") || "Email"}:</strong> {admin?.email}
          </p>
          <p className="text-sm text-gray-600">
            <strong>{t("city") || "City"}:</strong>{" "}
            {admin?.cities?.map((city) => city.cityName).join(", ")}
          </p>
          <p className="text-sm text-gray-600">
            <strong>{t("current_role") || "Current Role"}:</strong>{" "}
            {(() => {
              const roleKey = Object.keys(role).find(
                (key) => role[key] === admin?.roleId
              );
              return roleKey === "TerminalAdmin"
                ? "Terminal/City Admin"
                : roleKey;
            })()}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium mb-2">
              {t("role") || "New Role"} <span className="text-red-500">*</span>
            </label>
            <Select
              primaryColor="indigo"
              value={
                roleOptions.find((opt) => Number(opt.value) === selectedRole) ||
                null
              }
              onChange={handleRoleChange}
              options={roleOptions}
              classNames={{
                menu: "max-h-40 overflow-y-auto",
                list: "space-y-1",
                option: "py-1 px-2",
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isSuperAdmin ? t("change_role") : t("change_role_citizen")}
            </p>
          </div>

          {(selectedRole === role.superCityAdmin ||  selectedRole === role.TerminalAdmin ||
            selectedRole === role.contentCreator) &&
            isSuperAdmin && (
              <div>
                <label className="block font-medium mb-2">
                  {t("selectCity") || "Select City"}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Select
                  primaryColor="indigo"
                  value={selectedCity}
                  onChange={handleCityChange}
                  options={availableCitiesOptions}
                  placeholder={
                    t("select_assign_city") || "Select cities to assign"
                  }
                  classNames={{
                    menu: "max-h-40 overflow-y-auto",
                    list: "space-y-1",
                    option: "py-1 px-2",
                  }}
                  isMultiple={selectedRole === role.contentCreator || selectedRole === role.TerminalAdmin}
                  noOptionsMessage={
                    (selectedRole === role.contentCreator || selectedRole === role.TerminalAdmin) &&
                    availableCitiesOptions?.length === 0
                      ? t("noDataFound")
                      : null
                  }
                />
                <p className="text-xs text-gray-500 mt-1">
                 {selectedRole === role.superCityAdmin ?t("super_city_admin") || "Super City Admins manage one city": 
                 t("city_admin_citizen_manage_multi_city") || "City Admins and Citizen can manage multiple cities"}
                </p>
              </div>
            )}

          {error && <p className="text-sm text-red-500 text-center">{error}</p>}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
              onMouseDown={(e) => {
                e.preventDefault(); // ✅ KEY
                e.stopPropagation(); // ✅ KEY
                onClose(); // close modal immediately
              }}
              disabled={loading}
            >
              {t("cancel") || "Cancel"}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading || !selectedRole || ( isSuperAdmin && selectedCity?.length==0||!selectedCity) }
            >
              {loading
                ? t("saving...") || "Saving..."
                : t("update_role") || "Update Role"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminRoleModal;
